import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { type UpsertEventTariffRequest, UpsertEventTariffRequestSchema } from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// ── Types ──────────────────────────────────────────────────────────────────
//
// Clone of location-tariffs/ui/components/TariffForm.tsx, minus the nightly
// mode (events can never be priced per-night — ADR-5, design §5) and minus
// the guest/nonGuest fields (nightly-only). Same dollars↔cents boundary
// convention: react-hook-form works in DOLLARS, converted to integer cents
// at submit time via setValueAs.

export interface EventTariffFormValues {
  mode: 'free' | 'flat' | 'hourly';
  differentiatedByDay: boolean;
  /** Display value in dollars (string); converted to cents on submit. */
  amountCents: number | null;
  dailyCapCents: number | null;
  weekdayAmountCents: number | null;
  weekendAmountCents: number | null;
}

interface EventTariffFormProps {
  /**
   * Called with a fully-validated UpsertEventTariffRequest whose monetary
   * fields are INTEGER CENTS. The parent is responsible for calling the API.
   */
  onSubmit: (values: UpsertEventTariffRequest) => Promise<void>;
  /** Pre-populated defaults (monetary values already converted to dollars for display). */
  defaultValues?: Partial<EventTariffFormValues>;
  submitLabel?: string;
}

// ── Dollar ↔ Cent helpers ──────────────────────────────────────────────────

/** Display string → integer cents (used in setValueAs). */
function dollarsToCents(v: string | number | null | undefined): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

// ── Component ──────────────────────────────────────────────────────────────

export function EventTariffForm({ onSubmit, defaultValues, submitLabel }: EventTariffFormProps) {
  const { t } = useTranslation('events');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EventTariffFormValues>({
    resolver: zodResolver(UpsertEventTariffRequestSchema),
    defaultValues: {
      mode: 'flat',
      differentiatedByDay: false,
      amountCents: null,
      dailyCapCents: null,
      weekdayAmountCents: null,
      weekendAmountCents: null,
      ...defaultValues,
    },
  });

  const mode = watch('mode');
  const differentiated = watch('differentiatedByDay');

  async function handleFormSubmit(values: EventTariffFormValues) {
    setServerError(null);
    try {
      // Build the API body (cents are already stored as numbers via setValueAs).
      // We cast through unknown because EventTariffFormValues and
      // UpsertEventTariffRequest are structurally equivalent after
      // zodResolver transformation.
      await onSubmit(values as unknown as UpsertEventTariffRequest);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('eventTariffForm.genericError') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {serverError && <FormBanner message={serverError} />}

      {/* Mode selector — NO nightly option (events can never be priced per-night) */}
      <Field label={t('eventTariffForm.tariffMode')} htmlFor="mode" error={errors.mode?.message}>
        <select
          id="mode"
          disabled={isSubmitting}
          className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          {...register('mode')}
        >
          <option value="free">{t('eventTariffForm.modeOptions.free')}</option>
          <option value="flat">{t('eventTariffForm.modeOptions.flat')}</option>
          <option value="hourly">{t('eventTariffForm.modeOptions.hourly')}</option>
        </select>
      </Field>

      {/* Free mode hint */}
      {mode === 'free' && (
        <p className="text-[13px] text-jq-graphite">{t('eventTariffForm.freeHint')}</p>
      )}

      {/* Differentiation toggle — hidden when free */}
      {mode !== 'free' && (
        <Field
          label={t('eventTariffForm.differentiate')}
          htmlFor="differentiatedByDay"
          error={errors.differentiatedByDay?.message}
        >
          <div className="flex items-center gap-2">
            <input
              id="differentiatedByDay"
              type="checkbox"
              disabled={isSubmitting}
              className="h-4 w-4"
              {...register('differentiatedByDay')}
            />
            <span className="text-[13px] text-jq-graphite">
              {t('eventTariffForm.differentiateHint')}
            </span>
          </div>
        </Field>
      )}

      {/* Single amount field (non-differentiated, not free) */}
      {mode !== 'free' && !differentiated && (
        <Field
          label={mode === 'flat' ? t('eventTariffForm.flatFee') : t('eventTariffForm.ratePerHour')}
          htmlFor="amountCents"
          error={errors.amountCents?.message}
        >
          <Input
            id="amountCents"
            type="number"
            min={0}
            step="0.01"
            placeholder="0.00"
            disabled={isSubmitting}
            {...register('amountCents', {
              setValueAs: dollarsToCents,
            })}
          />
        </Field>
      )}

      {/* Differentiated amount fields */}
      {mode !== 'free' && differentiated && (
        <>
          <Field
            label={t('eventTariffForm.weekdayAmount')}
            htmlFor="weekdayAmountCents"
            error={errors.weekdayAmountCents?.message}
          >
            <Input
              id="weekdayAmountCents"
              type="number"
              min={0}
              step="0.01"
              placeholder="0.00"
              disabled={isSubmitting}
              {...register('weekdayAmountCents', {
                setValueAs: dollarsToCents,
              })}
            />
          </Field>

          <Field
            label={t('eventTariffForm.weekendAmount')}
            htmlFor="weekendAmountCents"
            error={errors.weekendAmountCents?.message}
          >
            <Input
              id="weekendAmountCents"
              type="number"
              min={0}
              step="0.01"
              placeholder="0.00"
              disabled={isSubmitting}
              {...register('weekendAmountCents', {
                setValueAs: dollarsToCents,
              })}
            />
          </Field>
        </>
      )}

      {/* Daily cap — hourly only */}
      {mode === 'hourly' && (
        <Field
          label={t('eventTariffForm.dailyCap.label')}
          htmlFor="dailyCapCents"
          error={errors.dailyCapCents?.message}
        >
          <Input
            id="dailyCapCents"
            type="number"
            min={0}
            step="0.01"
            placeholder={t('eventTariffForm.dailyCap.placeholder')}
            disabled={isSubmitting}
            {...register('dailyCapCents', {
              setValueAs: dollarsToCents,
            })}
          />
          <p className="mt-1 text-[12px] text-jq-graphite">{t('eventTariffForm.dailyCapHint')}</p>
        </Field>
      )}

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting
            ? t('eventTariffForm.saving')
            : (submitLabel ?? t('eventTariffForm.saveTariff'))}
        </Button>
      </div>
    </form>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
